import "./ThumbnailDistance.css";

import { Controller } from "@hotwired/stimulus";
import {
    browserLocationChangedEventName,
    browserLocationFromEvent,
    readRecentBrowserLocation,
    type SmartGuideBrowserLocation
} from "../BrowserLocation/SmartGuideBrowserLocation";
import {
    distanceInMeters,
    formatThumbnailDistance,
    isValidCoordinates
} from "./ThumbnailDistanceUi";
import { formatTranslation, webTranslations } from "../webTranslations";
import {
    destinationEntryChangedEventName,
    destinationEntryFromEvent
} from "../BrowserLocation/SmartGuideDestinationEntry";

type ThumbnailDistanceModel = {
    latitude?: number | null;
    longitude?: number | null;
    hasServerDistance: boolean;
    requireDestinationEntry?: boolean;
};

export default class ThumbnailDistanceController extends Controller<HTMLElement> {
    static values = {
        model: Object
    };

    declare readonly modelValue: ThumbnailDistanceModel;

    private isListening = false;
    private isInsideDestination = false;

    connect(): void {
        if (this.modelValue.hasServerDistance && !this.modelValue.requireDestinationEntry) {
            return;
        }

        if (!this.modelValue.requireDestinationEntry) {
            this.applyLocation(readRecentBrowserLocation({ sources: ["gps"] }));
        }
        document.addEventListener(browserLocationChangedEventName, this.onBrowserLocationChanged);
        if (this.modelValue.requireDestinationEntry) {
            document.addEventListener(destinationEntryChangedEventName, this.onDestinationEntryChanged);
        }
        this.isListening = true;
    }

    disconnect(): void {
        if (!this.isListening) {
            return;
        }

        document.removeEventListener(browserLocationChangedEventName, this.onBrowserLocationChanged);
        document.removeEventListener(destinationEntryChangedEventName, this.onDestinationEntryChanged);
        this.isListening = false;
        this.isInsideDestination = false;
    }

    private readonly onBrowserLocationChanged = (event: Event): void => {
        if (this.modelValue.requireDestinationEntry && !this.isInsideDestination) {
            return;
        }
        this.applyLocation(browserLocationFromEvent(event));
    };

    private readonly onDestinationEntryChanged = (event: Event): void => {
        const entry = destinationEntryFromEvent(event);
        if (!entry) {
            return;
        }

        this.isInsideDestination = entry.inside;
        if (!entry.inside) {
            this.hide();
            return;
        }

        this.applyLocation(entry.location);
    };

    private hide(): void {
        this.element.hidden = true;
        this.element.setAttribute("aria-hidden", "true");
        this.element.removeAttribute("aria-label");
    }

    private applyLocation(location: SmartGuideBrowserLocation | undefined): void {
        const { latitude, longitude } = this.modelValue;
        if (!location || latitude == null || longitude == null || !isValidCoordinates(latitude, longitude)) {
            return;
        }

        const text = formatThumbnailDistance(distanceInMeters(
            location.latitude,
            location.longitude,
            latitude,
            longitude
        ));
        const label = this.element.querySelector<HTMLElement>("[data-thumbnail-distance-label]");
        if (!text || !label) {
            return;
        }

        label.textContent = text;
        this.element.hidden = false;
        this.element.setAttribute("aria-hidden", "false");
        this.element.setAttribute("aria-label", formatTranslation(webTranslations.distanceFormat, text));
    }
}
